import React, { useState, useEffect } from 'react';
import { 
    Box, 
    TextField, 
    Typography, 
    Alert,
    Chip,
    Tooltip,
    LinearProgress,
    Stack,
    Button
} from '@mui/material';
import {
    CheckCircle as CheckIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { validateFile, getValidationSummary, formatFileSize } from '../../utils/fileValidation';
import { 
    validateAltText, 
    getAltTextValidationSummary, 
    getMaxAltTextLength,
    getAltTextGuidance,
    analyzeAltTextQuality 
} from '../../utils/altTextValidation';

/**
 * MediaPreview Component
 * Displays uploaded media with alt text input and file validation feedback
 * Following PostDetailsModal.js renderMedia patterns
 * Enhanced with production-ready file validation
 */
const MediaPreview = ({
    postImg,
    imgAltText,
    onAltTextChange,
    videoError,
    uploadedFile = null,
    selectedPlatforms = [],
    showValidation = true,
    onValidationChange = null,
    requireAltText = false,
    enforceAltTextQuality = false,
    showAltTextGuidance = false
}) => {
    const [validation, setValidation] = useState(null);
    const [validationSummary, setValidationSummary] = useState(null);
    const [altTextValidation, setAltTextValidation] = useState(null);
    const [altTextSummary, setAltTextSummary] = useState(null);
    const [showGuidance, setShowGuidance] = useState(showAltTextGuidance);

    // Validate file when uploadedFile or platforms change
    useEffect(() => {
        if (uploadedFile && showValidation) {
            const platforms = selectedPlatforms.map(p => p.platform || p);
            const validationResult = validateFile(uploadedFile, platforms);
            const summary = getValidationSummary(validationResult);
            
            setValidation(validationResult);
            setValidationSummary(summary);

            // Notify parent component of validation status
            if (onValidationChange) {
                onValidationChange(validationResult);
            }
        } else {
            setValidation(null);
            setValidationSummary(null);
        }
    }, [uploadedFile, selectedPlatforms, showValidation, onValidationChange]);

    // Validate alt text when it changes or platforms change
    useEffect(() => {
        if (postImg.url && postImg.type !== 'mp4') { // Only validate alt text for images
            const platforms = selectedPlatforms.map(p => p.platform || p);
            const altValidation = validateAltText(imgAltText, platforms, {
                requireAltText,
                enforceQuality: enforceAltTextQuality,
                minQualityScore: 50
            });
            const altSummary = getAltTextValidationSummary(altValidation);
            
            setAltTextValidation(altValidation);
            setAltTextSummary(altSummary);
        } else {
            setAltTextValidation(null);
            setAltTextSummary(null);
        }
    }, [imgAltText, selectedPlatforms, postImg.url, postImg.type, requireAltText, enforceAltTextQuality]);

    if (!postImg.url) {
        return null;
    }

    const renderValidationStatus = () => {
        if (!showValidation || !validation || !validationSummary) {
            return null;
        }

        const getStatusIcon = () => {
            switch (validationSummary.status) {
                case 'success':
                    return validationSummary.hasWarnings ? <WarningIcon /> : <CheckIcon />;
                case 'error':
                    return <ErrorIcon />;
                default:
                    return <InfoIcon />;
            }
        };

        return (
            <Box sx={{ mt: 1, mb: 2 }}>
                <Alert 
                    severity={validationSummary.color}
                    icon={getStatusIcon()}
                    sx={{ mb: 1 }}
                >
                    <Typography variant="body2">
                        {validationSummary.primaryMessage}
                    </Typography>
                    
                    {validation.warnings.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            {validation.warnings.map((warning, index) => (
                                <Typography key={index} variant="caption" display="block">
                                    • {warning}
                                </Typography>
                            ))}
                        </Box>
                    )}
                    
                    {validation.errors.length > 1 && (
                        <Box sx={{ mt: 1 }}>
                            {validation.errors.slice(1).map((error, index) => (
                                <Typography key={index} variant="caption" display="block" color="error">
                                    • {error}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Alert>

                {/* File info chips */}
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip 
                        size="small" 
                        label={`${validation.formattedSize} / ${validation.formattedMaxSize}`}
                        color={validation.isValid ? 'success' : 'error'}
                        variant="outlined"
                    />
                    
                    {selectedPlatforms.length > 0 && (
                        <Tooltip title={`Validated for: ${selectedPlatforms.map(p => p.platform || p).join(', ')}`}>
                            <Chip 
                                size="small" 
                                label={`${selectedPlatforms.length} platform${selectedPlatforms.length > 1 ? 's' : ''}`}
                                color="primary"
                                variant="outlined"
                            />
                        </Tooltip>
                    )}
                </Stack>
            </Box>
        );
    };

    const renderAltTextValidation = () => {
        if (!altTextValidation || !altTextSummary || postImg.type === 'mp4') {
            return null;
        }

        const getAltTextIcon = () => {
            switch (altTextSummary.status) {
                case 'success':
                    return altTextSummary.hasWarnings ? <WarningIcon /> : <CheckIcon />;
                case 'error':
                    return <ErrorIcon />;
                case 'info':
                    return <InfoIcon />;
                default:
                    return <InfoIcon />;
            }
        };

        return (
            <Box sx={{ mt: 1 }}>
                <Alert 
                    severity={altTextSummary.color}
                    icon={getAltTextIcon()}
                    sx={{ mb: 1 }}
                >
                    <Typography variant="body2">
                        {altTextSummary.primaryMessage}
                    </Typography>
                    
                    {altTextValidation.quality && altTextValidation.quality.score > 0 && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip 
                                size="small" 
                                label={`Quality: ${altTextValidation.quality.score}/100`}
                                color={altTextValidation.quality.score >= 70 ? 'success' : 
                                       altTextValidation.quality.score >= 40 ? 'warning' : 'error'}
                                variant="outlined"
                            />
                            
                            {altTextValidation.compliance.wcag && (
                                <Chip 
                                    size="small" 
                                    label="WCAG Compliant"
                                    color="success"
                                    variant="outlined"
                                />
                            )}
                        </Stack>
                    )}
                    
                    {altTextSummary.hasSuggestions && (
                        <Box sx={{ mt: 1 }}>
                            {altTextValidation.suggestions.slice(0, 2).map((suggestion, index) => (
                                <Typography key={index} variant="caption" display="block" color="text.secondary">
                                    • {suggestion}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Alert>
            </Box>
        );
    };

    const renderAltTextGuidance = () => {
        if (!showGuidance || postImg.type === 'mp4') {
            return null;
        }

        const guidance = getAltTextGuidance('post');

        return (
            <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Alt Text Writing Tips
                </Typography>
                
                <Typography variant="caption" color="text.secondary" paragraph>
                    Good alt text helps screen reader users understand your image:
                </Typography>
                
                <Box component="ul" sx={{ mt: 0, mb: 1, pl: 2 }}>
                    {guidance.tips.slice(0, 3).map((tip, index) => (
                        <Typography key={index} component="li" variant="caption" color="text.secondary">
                            {tip}
                        </Typography>
                    ))}
                </Box>
                
                {guidance.examples.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                            Example: 
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            "{guidance.examples[0]}"
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    const renderMedia = () => {
        if (postImg.type === 'mp4') {
            return (
                <Box>
                    <video 
                        width="100%" 
                        height="100%" 
                        controls
                        style={{ maxHeight: 300, borderRadius: 8 }}
                    >
                        <source src={postImg.url} type="video/mp4"/>
                        Your browser does not support the video tag.
                    </video>
                    {videoError && (
                        <Typography 
                            color="error" 
                            variant="caption" 
                            sx={{ 
                                display: 'block', 
                                mt: 1,
                                textAlign: 'center' 
                            }}
                        >
                            {videoError}
                        </Typography>
                    )}
                </Box>
            );
        }

        if (postImg.type === 'gif') {
            return (
                <img 
                    src={postImg.url} 
                    alt="Generated GIF"
                    style={{
                        width: '100%', 
                        height: '100%',
                        maxHeight: 300,
                        objectFit: 'cover',
                        borderRadius: 8
                    }}
                />
            );
        }

        return (
            <img 
                src={postImg.url} 
                alt={imgAltText || "Generated Image"}
                style={{
                    width: '100%', 
                    height: '100%',
                    maxHeight: 300,
                    objectFit: 'cover',
                    borderRadius: 8
                }}
            />
        );
    };

    return (
        <Box sx={{ width: 300, mb: 2 }}>
            {renderMedia()}
            
            {/* File Validation Status */}
            {renderValidationStatus()}
            
            {/* Enhanced Alt Text Input for Accessibility */}
            {postImg.type !== 'mp4' && (
                <Box sx={{ mt: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">
                            Image Description (Alt Text)
                        </Typography>
                        {requireAltText && (
                            <Chip 
                                size="small" 
                                label="Required" 
                                color="error" 
                                variant="outlined"
                            />
                        )}
                        <Tooltip title="Toggle writing guidance">
                            <Button 
                                size="small" 
                                onClick={() => setShowGuidance(!showGuidance)}
                                sx={{ minWidth: 'auto', p: 0.5 }}
                            >
                                <InfoIcon fontSize="small" />
                            </Button>
                        </Tooltip>
                    </Stack>

                    <TextField
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={2}
                        value={imgAltText || ''}
                        onChange={(e) => onAltTextChange(e.target.value)}
                        placeholder="Describe the image for screen readers..."
                        error={altTextSummary?.hasErrors}
                        helperText={
                            altTextSummary?.hasErrors 
                                ? altTextSummary.primaryMessage
                                : "Describe what's important about this image"
                        }
                        inputProps={{
                            maxLength: getMaxAltTextLength(selectedPlatforms.map(p => p.platform || p)),
                            'aria-describedby': 'alt-text-helper alt-text-validation'
                        }}
                    />
                    
                    {/* Character count and platform info */}
                    <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography 
                            variant="caption" 
                            color={altTextValidation?.isValid === false ? 'error' : 'text.secondary'}
                            id="alt-text-helper"
                        >
                            {imgAltText?.length || 0}/{getMaxAltTextLength(selectedPlatforms.map(p => p.platform || p))} characters
                        </Typography>
                        
                        {selectedPlatforms.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                Limit based on {selectedPlatforms.map(p => p.platform || p).join(', ')}
                            </Typography>
                        )}
                    </Box>

                    {/* Alt text validation feedback */}
                    {renderAltTextValidation()}

                    {/* Alt text writing guidance */}
                    {renderAltTextGuidance()}
                </Box>
            )}
        </Box>
    );
};

export default MediaPreview; 